import {
  AVG_ACTIVE_USERS,
  CHURN_ACCELERATION,
  CONTRACTION_BEFORE_CANCEL_ACCOUNTS,
  CUSTOMER_CHURN_RATE,
  LOGO_VS_DOLLAR_CHURN_GAP,
  NET_REVENUE_CHURN,
  PAYING_DORMANT,
  REVENUE_CHURN_RATE,
  SINGLE_USER_COUNT,
  SINGLE_USER_MRR_SHARE,
  ZOMBIE_REVENUE,
} from "./exampleData";
import { CHART_NEGATIVE, MUTED } from "./chartTheme";
import MetricBarChart from "./MetricBarChart";
import AmountBarChart from "./AmountBarChart";

const pct = (v: number) => `${v}%`;
const pts = (v: number) => `${v}pp`;
const count = (v: number) => `${v}`;
const users = (v: number) => `${v}`;
const zero = { y: 0 }; // the axis already shows 0 — line only

export const CustomerChurnRateChart = () => (
  <MetricBarChart
    caption="Customer churn rate hovers around 6% a month — the share of accounts that stop paying. It looks small, but compounded it means losing roughly half your customers over a year."
    data={CUSTOMER_CHURN_RATE}
    seriesName="Churn rate"
    format={pct}
    color={CHART_NEGATIVE}
    domain={[0, "dataMax + 3"]}
  />
);

export const RevenueChurnRateChart = () => (
  <MetricBarChart
    caption="Gross revenue churn runs at 10–12% of starting MRR a month — cancellations plus downgrades, before any expansion offsets. June's 10% is $2,000 lost from a $20,000 base."
    data={REVENUE_CHURN_RATE}
    seriesName="Revenue churn rate"
    format={pct}
    color={CHART_NEGATIVE}
    domain={[0, "dataMax + 3"]}
  />
);

export const NetRevenueChurnChart = () => (
  <MetricBarChart
    caption="Net revenue churn falls from +3% to −5% as expansion overtakes gross losses — below the zero line is negative churn, where the existing base grows on its own. June's −5% mirrors a 105% NRR."
    data={NET_REVENUE_CHURN}
    seriesName="Net revenue churn"
    format={pct}
    color={MUTED}
    reference={zero}
  />
);

export const ChurnAccelerationChart = () => (
  <MetricBarChart
    caption="Churn acceleration is the month-to-month change in the churn rate — bars above zero mean churn sped up, below zero it eased. Persistent positive readings are an early warning long before churn itself looks alarming."
    data={CHURN_ACCELERATION}
    seriesName="Churn acceleration"
    format={pts}
    color={MUTED}
    domain={[-1, 1]}
    reference={zero}
  />
);

export const LogoVsDollarChurnGapChart = () => (
  <MetricBarChart
    caption="The gap between logo churn and dollar churn sits around −3 to −5 points — negative means dollar churn outruns logo churn, so the customers you lose are bigger than average. A widening negative gap is a concentration warning."
    data={LOGO_VS_DOLLAR_CHURN_GAP}
    seriesName="Logo − dollar churn"
    format={pts}
    color={MUTED}
    domain={[-6, 1]}
    reference={zero}
  />
);

export const ContractionBeforeCancelChart = () => (
  <MetricBarChart
    caption="Accounts that cancelled within 90 days of a contraction run 3–6 a month — contraction as an early warning that turned into churn. Watching this bridge lets you intervene on shrinking accounts before they leave."
    data={CONTRACTION_BEFORE_CANCEL_ACCOUNTS}
    seriesName="At-risk accounts"
    format={count}
    color={CHART_NEGATIVE}
    domain={[0, "dataMax + 3"]}
    yWidth={28}
  />
);

export const AverageActiveUsersChart = () => (
  <MetricBarChart
    caption="Average active users per account climbs from 2.6 to 3.3. Multiplayer accounts retain and expand far better than single-seat ones, so a rising figure is one of the quietest good signs in SaaS."
    data={AVG_ACTIVE_USERS}
    seriesName="Users / account"
    format={users}
    domain={[0, "dataMax + 1"]}
    yWidth={32}
  />
);

export const SingleUserMrrShareChart = () => (
  <MetricBarChart
    caption="The share of MRR sitting in single-active-user accounts falls from 42% to 33% — revenue at acute risk, since if that one person leaves the account usually follows. Lower is safer."
    data={SINGLE_USER_MRR_SHARE}
    seriesName="Single-user MRR share"
    format={pct}
    color={MUTED}
    domain={[0, 100]}
  />
);

export const SingleUserCountChart = () => (
  <MetricBarChart
    caption="The count of accounts with only one active user drifts down from 55 to 46 — each is a single point of failure. Converting them to multi-user is one of the highest-leverage retention plays."
    data={SINGLE_USER_COUNT}
    seriesName="Single-user accounts"
    format={count}
    color={MUTED}
    domain={[0, "dataMax + 10"]}
    yWidth={32}
  />
);

export const PayingDormantChart = () => (
  <MetricBarChart
    caption="Paying dormant accounts run 12–16 a month — customers who still pay but have gone quiet in the product. They renew today and churn tomorrow, so this count is a leading indicator of future losses."
    data={PAYING_DORMANT}
    seriesName="Paying dormant"
    format={count}
    color={MUTED}
    domain={[0, "dataMax + 6"]}
    yWidth={28}
  />
);

export const ZombieRevenueChart = () => (
  <AmountBarChart
    caption="Zombie revenue — MRR from paying-but-dormant accounts — sits around $2,400–$3,200 a month. It counts as revenue today, but it is the most fragile MRR you have and the first to churn in a downturn."
    data={ZOMBIE_REVENUE}
    seriesName="Zombie revenue"
    color={MUTED}
  />
);
